import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './prescriptionAnalysisResult.css';
import LogUtil from '../../core/utils/LogUtil';

/**
 * 처방전 분석 결과 페이지
 * 처방전 이미지 + 처방 항목 테이블
 */
const PrescriptionAnalysisResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [prescriptionItems, setPrescriptionItems] = useState([]);

  const { receiptUrl, analysisResult } = location.state || {};

  useEffect(() => {
    // 데이터가 없으면 업로드 페이지로 리다이렉트
    if (!receiptUrl || !analysisResult) {
      navigate('/test/prescription');
      return;
    }

    // 처방전 약품 데이터 처리
    const medications = analysisResult.medications || [];

    const itemsWithId = medications.map((med, index) => ({
      id: `item-${index}-${Date.now()}`,
      약품코드: med.medication_code || '-',
      약품명: med.medication_name || '-',
      투약량: med.dosage_per_dose || '-',
      투여횟수: med.frequency_per_day || '-',
      투약일수: med.total_days || '-',
    }));

    setPrescriptionItems(itemsWithId);

    LogUtil.log('✅ [Prescription Result] 결과 로드 완료:', {
      총_항목: itemsWithId.length,
      데이터: itemsWithId,
    });
  }, [receiptUrl, analysisResult, navigate]);

  if (!receiptUrl || !analysisResult) {
    return null;
  }

  return (
    <div className="prescription-result-container">
      {/* 헤더 */}
      <div className="prescription-result-header">
        <h2 className="prescription-result-title">처방전 분석 결과</h2>
        <div className="prescription-action-buttons">
          <button
            onClick={() => navigate('/test/prescription')}
            className="prescription-back-button"
          >
            ← 새로 분석
          </button>
        </div>
      </div>

      {/* 컨텐츠 */}
      <div className="prescription-content-wrapper">
        {/* 처방전 이미지 + 항목 테이블 */}
        <div className="prescription-review-section">
          {/* 처방전 이미지 */}
          <div className="prescription-receipt-section">
            <div className="prescription-receipt-image-container">
              <img
                src={receiptUrl}
                alt="처방전"
                className="prescription-receipt-image"
                onClick={() => window.open(receiptUrl, '_blank')}
              />
              <div className="prescription-receipt-image-overlay">
                <span>클릭하여 확대</span>
              </div>
            </div>
          </div>

          {/* 오른쪽 섹션: 기본정보 + 약품목록 */}
          <div className="prescription-right-section">
            {/* 기본 정보 */}
            <div className="prescription-basic-info-section">
              <div className="prescription-items-header">
                <span className="prescription-items-title">기본 정보</span>
              </div>
              <div className="prescription-basic-info">
                <div className="prescription-info-row">
                  <span className="prescription-info-label">요양기관번호:</span>
                  <span className="prescription-info-value">
                    {analysisResult.medical_institution_number || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">교부일자:</span>
                  <span className="prescription-info-value">
                    {analysisResult.issue_date || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">교부번호:</span>
                  <span className="prescription-info-value">
                    {analysisResult.issue_number || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">성명:</span>
                  <span className="prescription-info-value">
                    {analysisResult.patient_name || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">주민등록번호:</span>
                  <span className="prescription-info-value">
                    {analysisResult.resident_number || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">질병기호:</span>
                  <span className="prescription-info-value">
                    {analysisResult.disease_code || '-'}
                  </span>
                </div>
                <div className="prescription-info-row">
                  <span className="prescription-info-label">의사면허번호:</span>
                  <span className="prescription-info-value">
                    {analysisResult.doctor_license_number || '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* 약품 목록 테이블 */}
            <div className="prescription-items-section">
              <div className="prescription-items-header">
                <span className="prescription-items-title">처방 약품</span>
                <span className="prescription-items-count">
                  {prescriptionItems.length}건
                </span>
              </div>
              <div className="prescription-table-container">
                {prescriptionItems.length > 0 ? (
                  <table className="prescription-item-table">
                    <thead>
                      <tr>
                        <th className="prescription-table-header">약품코드</th>
                        <th className="prescription-table-header">약품명</th>
                        <th className="prescription-table-header">투약량</th>
                        <th className="prescription-table-header">투여횟수</th>
                        <th className="prescription-table-header">투약일수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptionItems.map(item => (
                        <tr key={item.id} className="prescription-table-row">
                          <td className="prescription-table-cell">
                            {item.약품코드 || '-'}
                          </td>
                          <td className="prescription-table-cell">
                            {item.약품명 || '-'}
                          </td>
                          <td className="prescription-table-cell-center">
                            {item.투약량 || '-'}
                          </td>
                          <td className="prescription-table-cell-center">
                            {item.투여횟수 || '-'}
                          </td>
                          <td className="prescription-table-cell-center">
                            {item.투약일수 || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="prescription-empty-message">
                    항목이 없습니다
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionAnalysisResult;
